// src/pages/Auth/PhoneSignup.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
   Box,
   Card,
   CardContent,
   Typography,
   Button,
   Alert,
   CircularProgress,
   Step,
   Stepper,
   StepLabel,
   Paper,
   Container,
   Avatar,
} from '@mui/material';
import {
   CheckCircle as CheckCircleIcon,
   PersonAdd as PersonAddIcon,
   Login as LoginIcon,
} from '@mui/icons-material';
import {
   verifyPhoneInvitation,
   formatPhoneNumberForDisplay,
} from '../../services/phoneInvitationService';

const steps = ['Verify Invitation', 'Check Account', 'Complete', 'Welcome'];

interface InvitationData {
   id: string;
   phoneNumber: string;
   tripId?: string;
   eventId?: string;
   albumId?: string;
   tripTitle?: string;
   eventTitle?: string;
   albumTitle?: string;
   inviterName: string;
   role: string;
   type: 'trip' | 'event' | 'album';
   customMessage?: string;
   recipientName?: string;
}

const PhoneSignup: React.FC = () => {
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();

   // States
   const [activeStep, setActiveStep] = useState(0);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);

   // Invitation data
   const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
   const [token, setToken] = useState<string | null>(null);

   useEffect(() => {
      const invitationToken = searchParams.get('token');
      if (invitationToken) {
         setToken(invitationToken);
         verifyInvitation(invitationToken);
      } else {
         setError('Invalid invitation link');
      }
   }, [searchParams]);

   const verifyInvitation = async (invitationToken: string) => {
      setLoading(true);
      setError(null);

      try {
         const result = await verifyPhoneInvitation(invitationToken);

         if (result.success && result.invitation) {
            setInvitationData(result.invitation);
            setActiveStep(1); // Move to "Check Account" step
         } else {
            setError(result.error || 'Failed to verify invitation');
         }
      } catch (error: any) {
         setError('Failed to verify invitation. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   const checkUserAndProceed = async () => {
      if (!invitationData) return;

      setLoading(true);
      setError(null);

      try {
         // Check if user exists using Firebase Auth directly
         const userCheckResponse = await fetch('/api/check-user-exists', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               phoneNumber: invitationData.phoneNumber,
            }),
         });

         const userCheckData = await userCheckResponse.json();

         if (userCheckData.exists) {
            // User exists - redirect to login with invitation token
            setActiveStep(2);
            setSuccess('Account found! Redirecting to login...');

            setTimeout(() => {
               navigate(
                  `/login?phoneNumber=${encodeURIComponent(invitationData.phoneNumber)}&invitationToken=${encodeURIComponent(token || '')}`
               );
            }, 3000);
         } else {
            // User doesn't exist - redirect to signup
            setActiveStep(2);
            setSuccess('New user! Redirecting to signup...');

            setTimeout(() => {
               navigate(
                  `/signup?phoneNumber=${encodeURIComponent(invitationData.phoneNumber)}&invitationToken=${encodeURIComponent(token || '')}`
               );
            }, 3000);
         }
      } catch (error: any) {
         // If there's an error checking, default to signup flow
         setActiveStep(2);
         setSuccess('Proceeding to signup...');

         setTimeout(() => {
            navigate(
               `/signup?phoneNumber=${encodeURIComponent(invitationData?.phoneNumber || '')}&invitationToken=${encodeURIComponent(token || '')}`
            );
         }, 3000);
      } finally {
         setLoading(false);
      }
   };

   const getInvitationTitle = () => {
      if (!invitationData) return '';

      switch (invitationData.type) {
         case 'trip':
            return invitationData.tripTitle || 'Trip';
         case 'event':
            return invitationData.eventTitle || 'Event';
         case 'album':
            return invitationData.albumTitle || 'Album';
         default:
            return 'Item';
      }
   };

   const getInvitationIcon = () => {
      if (!invitationData) return '🎉';

      switch (invitationData.type) {
         case 'trip':
            return '✈️';
         case 'event':
            return '📅';
         case 'album':
            return '📸';
         default:
            return '🎉';
      }
   };

   if (loading && activeStep === 0) {
      return (
         <Container maxWidth='sm' sx={{ mt: 8 }}>
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   return (
      <Container maxWidth='sm' sx={{ mt: 4, mb: 4 }}>
         <Paper elevation={3} sx={{ overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
               <Typography variant='h4' component='h1' gutterBottom>
                  {getInvitationIcon()} You're Invited!
               </Typography>
               {invitationData && (
                  <Typography variant='h6'>
                     {invitationData.inviterName} invited you to join "{getInvitationTitle()}"
                  </Typography>
               )}
            </Box>

            {/* Stepper */}
            <Box sx={{ p: 2 }}>
               <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label, index) => (
                     <Step key={label}>
                        <StepLabel
                           StepIconComponent={({ active, completed }) => (
                              <Avatar
                                 sx={{
                                    bgcolor: completed
                                       ? 'success.main'
                                       : active
                                         ? 'primary.main'
                                         : 'grey.300',
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.875rem',
                                 }}
                              >
                                 {completed ? <CheckCircleIcon /> : index + 1}
                              </Avatar>
                           )}
                        >
                           {label}
                        </StepLabel>
                     </Step>
                  ))}
               </Stepper>
            </Box>

            {/* Content */}
            <CardContent sx={{ p: 4 }}>
               {error && (
                  <Alert severity='error' sx={{ mb: 3 }}>
                     {error}
                  </Alert>
               )}

               {success && (
                  <Alert severity='success' sx={{ mb: 3 }}>
                     {success}
                  </Alert>
               )}

               {activeStep === 1 && invitationData && (
                  <Box textAlign='center'>
                     <Typography variant='h5' gutterBottom>
                        Continue with Your Account
                     </Typography>

                     <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                        We'll check if you already have an account with this phone number
                     </Typography>

                     <Box
                        sx={{
                           p: 3,
                           bgcolor: 'grey.50',
                           borderRadius: 2,
                           mb: 3,
                           border: '1px solid',
                           borderColor: 'grey.200',
                        }}
                     >
                        <Typography variant='body1' sx={{ fontWeight: 'medium' }}>
                           Phone Number
                        </Typography>
                        <Typography variant='h6' color='primary.main'>
                           {formatPhoneNumberForDisplay(invitationData.phoneNumber)}
                        </Typography>
                     </Box>

                     {invitationData.customMessage && (
                        <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1, mb: 3 }}>
                           <Typography variant='body2' color='info.main' fontStyle='italic'>
                              "{invitationData.customMessage}"
                           </Typography>
                        </Box>
                     )}

                     <Button
                        variant='contained'
                        size='large'
                        onClick={checkUserAndProceed}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                        sx={{
                           minWidth: 200,
                           py: 1.5,
                           fontSize: '1.1rem',
                        }}
                     >
                        {loading ? 'Checking...' : 'Continue'}
                     </Button>

                     <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                        We'll redirect you to login if you have an account, or to signup if you're
                        new
                     </Typography>
                  </Box>
               )}

               {activeStep === 2 && (
                  <Box textAlign='center'>
                     <CheckCircleIcon color='success' sx={{ fontSize: 64, mb: 2 }} />
                     <Typography variant='h5' gutterBottom>
                        Redirecting...
                     </Typography>
                     <CircularProgress sx={{ mt: 2 }} />
                  </Box>
               )}
            </CardContent>
         </Paper>
      </Container>
   );
};

export default PhoneSignup;
